// Moving a café to Clavian from another platform (F-HQ-40; ADR-0048; spec/14 §3.5, spec/13 §8.2, spec/06 §2.3).
//
// HQ runs a migration on its own, from the organisation's page, for one venue: it picks the platform the café is
// leaving, uploads the final export taken after the old system was frozen, confirms how the file's columns map onto
// the Clavian migration format, maps the old PC groups onto the venue's station classes, answers whether the old
// system charged GST at top-up, reviews the dry run, ticks every balance above the wallet's maximum, and imports.
//
//   GET    /v1/hq/organizations/{o}/migrations                    the organisation's runs, newest first   hq:organizations
//   POST   /v1/hq/organizations/{o}/migrations                    start one: venue and platform           hq:organizations
//   GET    /v1/hq/organizations/{o}/migrations/{m}                one, with its mapping and dry run       hq:organizations
//   POST   /v1/hq/organizations/{o}/migrations/{m}/file           upload (or replace) its export          hq:organizations
//   PUT    /v1/hq/organizations/{o}/migrations/{m}/mapping        columns, units, classes, GST, switch    hq:organizations
//   GET    /v1/hq/organizations/{o}/migrations/{m}/rows           the validated rows, by status           hq:organizations
//   PUT    /v1/hq/organizations/{o}/migrations/{m}/ticks          HQ's ticks on balances above the cap    hq:organizations
//   POST   /v1/hq/organizations/{o}/migrations/{m}/import         import the next batches; call again     hq:organizations
//   POST   /v1/hq/organizations/{o}/migrations/{m}/undo           undo unclaimed records: all, or one     hq:organizations
//   GET    /v1/hq/organizations/{o}/migrations/{m}/report         the report, as CSV                      hq:organizations
//   GET    /v1/organizations/{o}/imported-players                 the venue's "Moved" list, read only     players:read
//
// **Imported players are never accounts.** An account is made only with its owner's consent (spec/07 §3.1); an
// imported player is an unclaimed profile — the old username, name, email (unverified), phone, date of birth, join
// date, last visit, staff note, ban — and the balances carried over for it, held in its own ledger accounts against
// the organisation's opening balance until the person claims it (F-IAM-13, `migration-claims.ts`).
//
// **What moves.** Real money — as "tax-paid" money when HQ answers that the old system charged GST at top-up, so it is
// never taxed twice. Prepaid time, as a time pass on the station classes HQ maps the old PC groups to, valid for
// `migration.carriedOverTimeValidityDays` (180) from the switch-over day. **Bonus money is dropped** and its total
// shown, so HQ can tell the café. Loyalty points, passwords and card data never move.
//
// **The import is idempotent and resumable.** A record is keyed by its platform and its old member id; one already
// imported from that platform is skipped, never doubled, in this run or any later one. Each call imports batches of
// rows, each batch in one transaction with its ledger entries and audit rows, until a time budget is spent, and says
// how far it got; the console calls again until the run is `completed`. A crash loses at most the batch in flight,
// which the next call imports. Undo reverses an unclaimed record's balances with reversing entries — never a delete —
// and is audited.
//
// Platform scope: every HQ route needs a platform role holding `hq:organizations` and a second factor on the session.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { ImportedPlayerIdSchema, MigrationRunIdSchema, StationClassIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_ORGANIZATIONS, PLAYERS_READ, PLAYERS_READ_CONTACT } from "../permissions/index.js";
import { MoneySchema } from "../primitives/money.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema, LocalDateSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { CSV_CONTENT_TYPE, PERMISSION_EXTENSION, UNMASKED_BY_EXTENSION } from "./audit.js";
// ---------------------------------------------------------------------------------------------
// The vocabulary
// ---------------------------------------------------------------------------------------------
/** Where a café is moving from. `other` is anything not listed. Each reads through a template (`MIGRATION_TEMPLATES`). */
export const MIGRATION_SOURCE_PLATFORMS = [
    'ggleap',
    'gizmo',
    'senet',
    'icafecloud',
    'pancafe',
    'smartlaunch',
    'antamedia',
    'handycafe',
    'truecafe',
    'other',
];
/** How a platform's export is read. Only the generic Clavian CSV exists today; a platform adapter adds one. */
export const MIGRATION_TEMPLATES = ['clavian_csv'];
/** The Clavian migration format's fields: one row per player. */
export const MIGRATION_FIELDS = [
    'sourceId',
    'username',
    'firstName',
    'lastName',
    'email',
    'phone',
    'dateOfBirth',
    'joinedOn',
    'lastVisitOn',
    'cashBalance',
    'bonusBalance',
    'timeBalance',
    'timePcGroups',
    'currency',
    'banned',
    'banReason',
    'note',
    'deleted',
];
/** Every finding a validated row can carry; its severity is fixed per code (`@clavian/domain`). */
export const MIGRATION_ISSUE_CODES = [
    'missing-username',
    'bad-amount',
    'bad-time',
    'other-currency',
    'duplicate-source-id',
    'username-clash',
    'time-group-unmapped',
    'negative-cash',
    'negative-time',
    'over-wallet-cap',
    'duplicate-email',
    'duplicate-phone',
    'email-invalid',
    'email-blocked',
    'phone-invalid',
    'date-invalid',
    'flag-unclear',
    'bad-bonus',
    'minor',
    'house-account',
    'deleted-in-source',
    'large-balance',
    'bonus-dropped',
    'existing-account',
    'counter-only',
    'already-imported',
];
export const MIGRATION_ISSUE_SEVERITIES = ['error', 'warning', 'info'];
/**
 * A run's life: `draft` until its file is read and its mapping is complete, `ready` to import, `importing` once begun
 * (until every row is done — resumable), `completed`, and `undone` once every unclaimed record was undone.
 */
export const MIGRATION_RUN_STATUSES = ['draft', 'ready', 'importing', 'completed', 'undone'];
/** A validated row: `ok` and `warning` are imported; `error` waits for a fixed file; `skipped` has nothing to import. */
export const MIGRATION_ROW_STATUSES = ['ok', 'warning', 'error', 'skipped', 'imported', 'undone'];
/** An imported player's state: waiting, claimed by its owner (F-IAM-13), undone by HQ, or erased after the window. */
export const IMPORTED_PLAYER_STATUSES = ['unclaimed', 'claimed', 'undone', 'erased'];
/** The ways a moved profile is claimed (F-IAM-13, ADR-0048 §9): at a PC, at the counter, by the moved email's link, or offered on sign-in. */
export const CARRIED_OVER_CLAIM_VIAS = ['kiosk', 'counter', 'email', 'auto_email'];
export const MIGRATION_MONEY_UNITS = ['major', 'minor'];
export const MIGRATION_TIME_UNITS = ['seconds', 'minutes', 'hours', 'hms'];
export const MIGRATION_DATE_ORDERS = ['dmy', 'mdy', 'ymd'];
/** The largest export one upload takes: 15 MB of CSV or XLSX. A bigger café splits nothing — it asks HQ to. */
export const MIGRATION_MAX_FILE_BYTES = 15 * 1024 * 1024;
/** The most rows one export may hold. */
export const MIGRATION_MAX_ROWS = 100_000;
// ---------------------------------------------------------------------------------------------
// The shapes
// ---------------------------------------------------------------------------------------------
export const MigrationSourcePlatformSchema = z.enum(MIGRATION_SOURCE_PLATFORMS).openapi('MigrationSourcePlatform');
export const MigrationTemplateSchema = z.enum(MIGRATION_TEMPLATES).openapi('MigrationTemplate');
export const MigrationFieldSchema = z.enum(MIGRATION_FIELDS).openapi('MigrationField');
export const MigrationIssueCodeSchema = z.enum(MIGRATION_ISSUE_CODES).openapi('MigrationIssueCode');
export const MigrationRunStatusSchema = z.enum(MIGRATION_RUN_STATUSES).openapi('MigrationRunStatus');
export const MigrationRowStatusSchema = z.enum(MIGRATION_ROW_STATUSES).openapi('MigrationRowStatus');
export const ImportedPlayerStatusSchema = z.enum(IMPORTED_PLAYER_STATUSES).openapi('ImportedPlayerStatus');
export const CarriedOverClaimViaSchema = z.enum(CARRIED_OVER_CLAIM_VIAS).openapi('CarriedOverClaimVia');
const columnIndex = z.int().min(0).max(199);
/** Which source column feeds each Clavian field (by its position in the header), and how the file writes values. */
/** For each Clavian field, the position of the source column that feeds it; absent when none does. */
export const MigrationColumnsSchema = z
    .object(Object.fromEntries(MIGRATION_FIELDS.map((field) => [field, columnIndex.optional()])))
    .strict()
    .openapi('MigrationColumns');
export const MigrationMappingSchema = z
    .object({
    columns: MigrationColumnsSchema,
    /** `major`: rupees with paise ("1,234.50"). `minor`: the column is already paise. */
    moneyUnit: z.enum(MIGRATION_MONEY_UNITS),
    /** How the time column is written: seconds, minutes, hours, or `h:mm`. */
    timeUnit: z.enum(MIGRATION_TIME_UNITS),
    /** The order of slash and dash dates (ISO dates are always read as ISO). */
    dateOrder: z.enum(MIGRATION_DATE_ORDERS),
})
    .strict()
    .openapi('MigrationMapping');
/** Old PC groups → the venue's station classes the carried-over time is valid on (owner's decision 3). */
/** One old PC group and the station classes its prepaid time is valid on. */
export const MigrationClassGroupSchema = z
    .object({ group: z.string().min(1).max(120), stationClassIds: z.array(StationClassIdSchema).max(50) })
    .strict()
    .openapi('MigrationClassGroup');
export const MigrationClassMapSchema = z
    .object({
    groups: z.array(MigrationClassGroupSchema).max(200),
    /** For time with no PC group given. */
    fallback: z.array(StationClassIdSchema).max(50),
})
    .strict()
    .openapi('MigrationClassMap');
export const MigrationFileSchema = z
    .object({
    name: z.string(),
    format: z.enum(['csv', 'xlsx']),
    /** SHA-256 of the bytes uploaded, hex. The file itself is not kept: its rows are. */
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    bytes: z.int().min(0),
    rows: z.int().min(0),
    uploadedAt: IsoDateTimeSchema,
})
    .strict()
    .openapi('MigrationFile');
export const MigrationLargeBalanceSchema = z
    .object({
    rowNumber: z.int().min(1),
    sourceKey: z.string(),
    username: z.string().nullable(),
    cash: MoneySchema,
    timeSeconds: z.int().min(0),
    /** Above the wallet's maximum: HQ must tick it before the import runs. */
    needsTick: z.boolean(),
    ticked: z.boolean(),
})
    .strict()
    .openapi('MigrationLargeBalance');
/** How many rows of each status the validation gave. */
export const MigrationStatusCountsSchema = z
    .object({ ok: z.int().min(0), warning: z.int().min(0), error: z.int().min(0), skipped: z.int().min(0) })
    .strict()
    .openapi('MigrationStatusCounts');
/** The dry run: what the import would post, computed from the validated rows; nothing is posted by it. */
export const MigrationSummarySchema = z
    .object({
    rows: z.int().min(0),
    byStatus: MigrationStatusCountsSchema,
    /** Players that will be imported (`ok` and `warning`). */
    players: z.int().min(0),
    /** Money carried over. */
    cash: MoneySchema,
    /** Of it, money GST was already paid on at top-up in the old system. */
    cashTaxPaid: MoneySchema,
    /** Prepaid time carried over as time passes. */
    timeSeconds: z.int().min(0),
    timePlayers: z.int().min(0),
    /** Bonus money dropped: tell the café. */
    bonusDropped: MoneySchema,
    /** Money players owed the café in the old system: listed for the desk, never carried over. */
    owedNotCarried: MoneySchema,
    minors: z.int().min(0),
    claimableByEmail: z.int().min(0),
    counterOnly: z.int().min(0),
    existingAccounts: z.int().min(0),
    largeBalances: z.array(MigrationLargeBalanceSchema),
    needsTick: z.int().min(0),
    issueCounts: z.record(z.string(), z.int().min(0)),
})
    .strict()
    .openapi('MigrationSummary');
/** What the ledger holds for the run's imported players, read back from it — the report's totals. */
export const MigrationPostedSchema = z
    .object({
    players: z.int().min(0),
    cash: MoneySchema,
    cashTaxPaid: MoneySchema,
    timeSeconds: z.int().min(0),
    /** Records undone, and what their reversing entries took back. */
    undone: z.int().min(0),
    claimed: z.int().min(0),
})
    .strict()
    .openapi('MigrationPosted');
/** How far an import has got: rows to import in all, imported, skipped (already imported elsewhere), still to go. */
export const MigrationProgressSchema = z
    .object({ toImport: z.int().min(0), imported: z.int().min(0), skipped: z.int().min(0), remaining: z.int().min(0) })
    .strict()
    .openapi('MigrationProgress');
/** Where a run's "account moved" email stands (F-IAM-13, ADR-0048 §13). */
export const MigrationMovedEmailSchema = z
    .object({
    round: z.int().min(0),
    requestedAt: IsoDateTimeSchema.nullable(),
    sent: z.int().min(0),
    pending: z.int().min(0),
})
    .strict()
    .openapi('MigrationMovedEmail');
export const MigrationStationClassSchema = z.object({ id: StationClassIdSchema, name: z.string() }).strict().openapi('MigrationStationClass');
export const MigrationRunSchema = z
    .object({
    id: MigrationRunIdSchema,
    venueId: VenueIdSchema,
    venueName: z.string(),
    sourcePlatform: MigrationSourcePlatformSchema,
    template: MigrationTemplateSchema,
    status: MigrationRunStatusSchema,
    /** The file in use, and every one uploaded before it (newest last). */
    file: MigrationFileSchema.nullable(),
    files: z.array(MigrationFileSchema),
    /** The file's header row, and its first rows as written, for the mapping step. */
    headers: z.array(z.string()),
    sampleRows: z.array(z.array(z.string())),
    /** The template's suggestion from the headers. */
    suggestedMapping: MigrationMappingSchema.nullable(),
    mapping: MigrationMappingSchema.nullable(),
    /** Every old PC group the file names, for the class mapping. */
    pcGroups: z.array(z.string()),
    classMap: MigrationClassMapSchema.nullable(),
    stationClasses: z.array(MigrationStationClassSchema),
    /** Did the old system charge GST at top-up? Null until HQ answers; always `false` for a venue not registered for GST. */
    gstPaidAtTopUp: z.boolean().nullable(),
    /**
     * The venue's tax mode on the switch-over day (today until one is set; spec/06 §10.3). A venue that is not registered
     * for GST (`unregistered`, plain bills) is never asked the GST question: its balances move as ordinary money.
     */
    venueTaxMode: z.enum(['regular', 'composition', 'unregistered']),
    /** Whether the GST question applies: the venue is registered for GST on the switch-over day. */
    gstQuestion: z.boolean(),
    /** The switch-over day, venue-local: ages are taken on it; time passes run from it. */
    switchOn: LocalDateSchema.nullable(),
    summary: MigrationSummarySchema.nullable(),
    ticked: z.array(z.string()),
    progress: MigrationProgressSchema,
    posted: MigrationPostedSchema.nullable(),
    /** The wallet is off at this organisation: money cannot be carried over until it is on. */
    walletEnabled: z.boolean(),
    lastError: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    importStartedAt: IsoDateTimeSchema.nullable(),
    completedAt: IsoDateTimeSchema.nullable(),
    undoneAt: IsoDateTimeSchema.nullable(),
    /** Until when the imported players wait to be claimed (`migration.claimWindowDays` from the switch). */
    claimWindowEndsOn: LocalDateSchema.nullable(),
    /**
     * The "account moved" email (F-IAM-13): which round is going out (0 before HQ first sends it; a resend starts the
     * next), when HQ last asked for it, how many waiting profiles have had this round, and how many with an email still
     * wait for it.
     */
    movedEmail: MigrationMovedEmailSchema,
})
    .strict()
    .openapi('MigrationRun');
export const MigrationRunListSchema = paginated(MigrationRunSchema, 'MigrationRunList');
export const CreateMigrationRunSchema = z
    .object({ venueId: VenueIdSchema, sourcePlatform: MigrationSourcePlatformSchema })
    .strict()
    .openapi('CreateMigrationRun');
/** The export, in the request: base64 of the bytes. CSV (comma, semicolon or tab) or XLSX (its first sheet). */
export const UploadMigrationFileSchema = z
    .object({
    fileName: z.string().min(1).max(200),
    contentBase64: z.string().min(4).max(Math.ceil((MIGRATION_MAX_FILE_BYTES * 4) / 3) + 4),
})
    .strict()
    .openapi('UploadMigrationFile');
export const UpdateMigrationMappingSchema = z
    .object({
    mapping: MigrationMappingSchema,
    classMap: MigrationClassMapSchema,
    gstPaidAtTopUp: z.boolean().nullable(),
    switchOn: LocalDateSchema,
})
    .strict()
    .openapi('UpdateMigrationMapping');
export const MigrationIssueSchema = z
    .object({ code: MigrationIssueCodeSchema, severity: z.enum(MIGRATION_ISSUE_SEVERITIES), field: MigrationFieldSchema.optional() })
    .strict()
    .openapi('MigrationIssue');
export const MigrationRowSchema = z
    .object({
    rowNumber: z.int().min(1),
    sourceKey: z.string(),
    username: z.string().nullable(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    cash: MoneySchema,
    bonus: MoneySchema,
    timeSeconds: z.int(),
    minor: z.boolean(),
    status: MigrationRowStatusSchema,
    issues: z.array(MigrationIssueSchema),
    importedPlayerId: ImportedPlayerIdSchema.nullable(),
})
    .strict()
    .openapi('MigrationRow');
export const MigrationRowListSchema = paginated(MigrationRowSchema, 'MigrationRowList');
export const MigrationRowQuerySchema = PageQuerySchema.extend({
    status: MigrationRowStatusSchema.optional(),
    issue: MigrationIssueCodeSchema.optional(),
})
    .strict()
    .openapi('MigrationRowQuery');
export const MigrationTicksSchema = z
    .object({ sourceKeys: z.array(z.string().min(1).max(140)).max(10_000) })
    .strict()
    .openapi('MigrationTicks');
export const UndoMigrationSchema = z
    .object({
    /** Why: kept in the audit row. */
    reason: z.string().trim().min(3).max(500),
    /** One record; absent, every unclaimed record of the run. */
    importedPlayerId: ImportedPlayerIdSchema.optional(),
})
    .strict()
    .openapi('UndoMigration');
/** A record an undo left alone because its owner had claimed it. */
export const MigrationClaimedRecordSchema = z
    .object({ id: ImportedPlayerIdSchema, username: z.string() })
    .strict()
    .openapi('MigrationClaimedRecord');
export const UndoMigrationResultSchema = z
    .object({
    undone: z.int().min(0),
    /** Records already claimed: not undone, listed for a person to resolve. */
    claimed: z.array(MigrationClaimedRecordSchema),
    run: MigrationRunSchema,
})
    .strict()
    .openapi('MigrationUndoOutcome');
/** An imported player, as the venue's staff see it in the "Moved" list. */
export const ImportedPlayerSchema = z
    .object({
    id: ImportedPlayerIdSchema,
    venueId: VenueIdSchema,
    sourcePlatform: MigrationSourcePlatformSchema,
    username: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    /** Unverified: it came from the old system. Masked without `players:read_contact`. */
    email: z.string().nullable(),
    phone: z.string().nullable(),
    /** Under 18 at the switch-over. The date of birth itself is never listed. */
    minor: z.boolean(),
    joinedOn: LocalDateSchema.nullable(),
    lastVisitOn: LocalDateSchema.nullable(),
    /** The old system's staff note: only with `players:notes` (as a player's notes); null otherwise. */
    note: z.string().nullable(),
    banned: z.boolean(),
    /** Why the old system banned them: only with `players:ban` (as a player's bans); null otherwise. */
    banReason: z.string().nullable(),
    status: ImportedPlayerStatusSchema,
    /** What is held for the person now, read from the ledger. */
    cash: MoneySchema,
    /** Of `cash`, money GST was already paid on at top-up. */
    cashTaxPaid: MoneySchema,
    timeSeconds: z.int().min(0),
    timeValidUntil: LocalDateSchema.nullable(),
    switchedOn: LocalDateSchema,
    claimWindowEndsOn: LocalDateSchema,
    /** When and how it was claimed (F-IAM-13); null (and no `claimVia`) while it waits. */
    claimedAt: IsoDateTimeSchema.nullable(),
    claimVia: CarriedOverClaimViaSchema.optional(),
    /** When the owner wrote its money off at the end of the window; null otherwise. */
    writtenOffAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
})
    .strict()
    .openapi('ImportedPlayer');
export const ImportedPlayerListSchema = paginated(ImportedPlayerSchema, 'ImportedPlayerList');
export const ImportedPlayerQuerySchema = PageQuerySchema.extend({
    /** Old username, name, email or phone — any part of a name or username, a whole email or phone. */
    q: z.string().trim().min(1).max(120).optional(),
    status: ImportedPlayerStatusSchema.optional(),
    venueId: VenueIdSchema.optional(),
})
    .strict()
    .openapi('ImportedPlayerQuery');
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const json = (schema) => ({ body: { content: { 'application/json': { schema } }, required: true } });
const RunParamsSchema = z.object({ organizationId: OrganizationScopeParamsSchema.shape.organizationId, migrationId: MigrationRunIdSchema }).openapi('MigrationRunParams');
const forbidden = problemResponse('Not HQ, no `hq:organizations`, or no second factor on the session.', 'forbidden', 'second-factor-required');
const noSuchRun = problemResponse('No such organisation, venue or migration.', 'not-found');
const hqResponses = (schema, description, extra = {}) => ({
    200: jsonResponse(schema, description),
    400: commonProblemResponses[400],
    401: commonProblemResponses[401],
    403: forbidden,
    404: noSuchRun,
    429: commonProblemResponses[429],
    500: commonProblemResponses[500],
    ...extra,
});
export const listMigrationRunsRoute = createRoute({
    method: 'get',
    path: '/v1/hq/organizations/{organizationId}/migrations',
    operationId: 'listMigrationRuns',
    tags: ['hq'],
    summary: 'The organisation’s migrations, newest first',
    description: 'Every move of this organisation’s players from another platform. Platform scope: `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: OrganizationScopeParamsSchema, query: PageQuerySchema },
    responses: hqResponses(MigrationRunListSchema, 'A page of runs.'),
});
export const createMigrationRunRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations/{organizationId}/migrations',
    operationId: 'createMigrationRun',
    tags: ['hq'],
    summary: 'Start a migration for one venue',
    description: 'Names the venue and the platform the café is leaving. Audited. Platform scope: `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: OrganizationScopeParamsSchema, ...json(CreateMigrationRunSchema) },
    responses: {
        ...hqResponses(MigrationRunSchema, 'The run, in `draft`.'),
        201: jsonResponse(MigrationRunSchema, 'The run, in `draft`.'),
        409: problemResponse('The venue already has a migration in progress.', 'conflict'),
    },
});
export const getMigrationRunRoute = createRoute({
    method: 'get',
    path: '/v1/hq/organizations/{organizationId}/migrations/{migrationId}',
    operationId: 'getMigrationRun',
    tags: ['hq'],
    summary: 'One migration: its file, mapping, dry run and progress',
    description: 'Platform scope: `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: RunParamsSchema },
    responses: hqResponses(MigrationRunSchema, 'The run.'),
});
export const uploadMigrationFileRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations/{organizationId}/migrations/{migrationId}/file',
    operationId: 'uploadMigrationFile',
    tags: ['hq'],
    summary: 'Upload the export (CSV or XLSX), replacing any before it',
    description: `The bytes as base64, at most ${MIGRATION_MAX_FILE_BYTES / 1024 / 1024} MB and ${MIGRATION_MAX_ROWS} rows. Read on the server, in chunks: ` +
        'nothing in it is executed (an XLSX cell\'s formula is never evaluated; its stored value is read as text). The rows ' +
        'are kept for validation; the file itself is not — its SHA-256 is. A file whose bytes were already uploaded to this run ' +
        'is accepted and changes nothing. Only before the import begins. Audited. Platform scope: `hq:organizations` and a ' +
        'second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: RunParamsSchema, ...json(UploadMigrationFileSchema) },
    responses: hqResponses(MigrationRunSchema, 'The run, with the file’s headers, sample rows and suggested mapping.', {
        409: problemResponse('The import has begun: the file can no longer change.', 'conflict'),
        422: problemResponse('The file is too large, cannot be read as CSV or XLSX, or has too many rows.', 'migration-file-unreadable'),
    }),
});
export const updateMigrationMappingRoute = createRoute({
    method: 'put',
    path: '/v1/hq/organizations/{organizationId}/migrations/{migrationId}/mapping',
    operationId: 'updateMigrationMapping',
    tags: ['hq'],
    summary: 'Set the column mapping, units, station classes, the GST answer and the switch-over day; validate',
    description: 'Validates every row against them and returns the dry run. The run is `ready` once the username is mapped and the ' +
        'GST question is answered. The question is asked only of a venue registered for GST on the switch-over day: for a ' +
        'venue that is not (plain bills), `gstPaidAtTopUp: true` is refused `400` and every balance moves as ordinary money. ' +
        'Station classes must be the venue’s own. Only before the import begins. Audited. ' +
        'Platform scope: `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: RunParamsSchema, ...json(UpdateMigrationMappingSchema) },
    responses: hqResponses(MigrationRunSchema, 'The run with its dry run.', {
        409: problemResponse('There is no file yet, or the import has begun.', 'conflict'),
    }),
});
export const listMigrationRowsRoute = createRoute({
    method: 'get',
    path: '/v1/hq/organizations/{organizationId}/migrations/{migrationId}/rows',
    operationId: 'listMigrationRows',
    tags: ['hq'],
    summary: 'The validated rows, in file order, filtered by status or issue',
    description: 'Platform scope: `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: RunParamsSchema, query: MigrationRowQuerySchema },
    responses: hqResponses(MigrationRowListSchema, 'A page of rows.'),
});
export const setMigrationTicksRoute = createRoute({
    method: 'put',
    path: '/v1/hq/organizations/{organizationId}/migrations/{migrationId}/ticks',
    operationId: 'setMigrationTicks',
    tags: ['hq'],
    summary: 'HQ’s ticks on the balances above the wallet’s maximum',
    description: 'The full set of ticked rows, by source key: each balance above `wallet.maxBalanceAmount` needs its own tick before the ' +
        'import runs (owner’s decision, ADR-0048). Audited with the amounts ticked. Platform scope: `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: RunParamsSchema, ...json(MigrationTicksSchema) },
    responses: hqResponses(MigrationRunSchema, 'The run.', { 409: problemResponse('The import has begun.', 'conflict') }),
});
export const importMigrationRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations/{organizationId}/migrations/{migrationId}/import',
    operationId: 'importMigration',
    tags: ['hq'],
    summary: 'Import the next batches of the run',
    description: 'Imports rows in batches — each batch one transaction: the imported players, their opening-balance entries and one audit ' +
        'row each — until a time budget is spent, then returns the run with its progress. Call again until `status` is ' +
        '`completed`; a call after a crash resumes from the first row not imported. Idempotent: a record already imported from ' +
        'this platform is never imported twice. Refused until the run is `ready`, every balance above the cap is ticked and the ' +
        'GST question is answered. Platform scope: `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: RunParamsSchema },
    responses: hqResponses(MigrationRunSchema, 'The run, with its progress.', {
        409: problemResponse('Not ready, a tick is missing, the GST question is unanswered, or the wallet is off while money is carried over.', 'conflict', 'migration-ticks-missing', 'migration-gst-unanswered', 'wallet-disabled'),
    }),
});
export const undoMigrationRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations/{organizationId}/migrations/{migrationId}/undo',
    operationId: 'undoMigration',
    tags: ['hq'],
    summary: 'Undo unclaimed records: the whole run, or one',
    description: 'Reverses each unclaimed record’s opening-balance entry with a reversing entry (never a delete) and marks it undone, one ' +
        'audit row each with the reason. Claimed records are not touched; they are listed. Platform scope: `hq:organizations` and ' +
        'a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: RunParamsSchema, ...json(UndoMigrationSchema) },
    responses: hqResponses(UndoMigrationResultSchema, 'What was undone, and what was not.', {
        409: problemResponse('The import has not begun, or the record is not unclaimed.', 'conflict'),
    }),
});
export const getMigrationReportRoute = createRoute({
    method: 'get',
    path: '/v1/hq/organizations/{organizationId}/migrations/{migrationId}/report',
    operationId: 'getMigrationReport',
    tags: ['hq'],
    summary: 'The run’s report, as CSV',
    description: 'One line per row of the file — its status, its issues, what was carried over and the imported player — and the totals read ' +
        'back from the ledger. For the café: it carries the players’ contact details, so reading it writes an audit row. Cells that ' +
        'would start a formula are neutralised. Platform scope: `hq:organizations` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_ORGANIZATIONS,
    request: { params: RunParamsSchema },
    responses: {
        200: {
            description: 'The report.',
            headers: { 'Content-Disposition': { description: 'attachment; filename="…csv"', schema: { type: 'string' } } },
            content: { [CSV_CONTENT_TYPE]: { schema: z.string() } },
        },
        401: commonProblemResponses[401],
        403: forbidden,
        404: noSuchRun,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listImportedPlayersRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/imported-players',
    operationId: 'listImportedPlayers',
    tags: ['players'],
    summary: 'Players moved from the café’s previous system',
    description: 'Unclaimed profiles brought over by a migration, with what is held for each, newest import first; `venueId` narrows to one ' +
        'venue (a member scoped to venues sees only theirs). Search by old username, name, email or phone. A row is claimed at the ' +
        'counter with `…/claim` (F-IAM-13). As the player list: contact details are masked without `players:read_contact`, the date of birth is never ' +
        'shown (only the under-18 flag), the old staff note needs `players:notes` and the ban reason `players:ban`. Requires `players:read`.',
    [PERMISSION_EXTENSION]: PLAYERS_READ,
    [UNMASKED_BY_EXTENSION]: PLAYERS_READ_CONTACT,
    request: { params: OrganizationScopeParamsSchema, query: ImportedPlayerQuerySchema },
    responses: {
        200: jsonResponse(ImportedPlayerListSchema, 'A page of imported players.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation, or no such venue in it.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/** Every migration route, for the contract index. */
export const MIGRATION_ROUTES = [
    // First, so the imported player's id is registered as the id it is, before a row names it nullable.
    listImportedPlayersRoute,
    listMigrationRunsRoute,
    createMigrationRunRoute,
    getMigrationRunRoute,
    uploadMigrationFileRoute,
    updateMigrationMappingRoute,
    listMigrationRowsRoute,
    setMigrationTicksRoute,
    importMigrationRoute,
    undoMigrationRoute,
    getMigrationReportRoute,
];
//# sourceMappingURL=migrations.js.map